import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Download,
  ExternalLink,
  Eye,
  GraduationCap,
  PlayCircle,
  Search,
  TriangleAlert,
} from "lucide-react";

import { EmptyPanel, PageHero } from "@/components/interior-page";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  MASTERMIND_CONTENT_TYPE_LABELS,
  MASTERMIND_FALLBACK_THUMBNAIL,
  fetchPublishedResources,
  formatFileSize,
  formatPublishedDate,
  openResourceFile,
  resourceThumbnail,
  type ResourceContent,
} from "@/lib/resources";

/** Content types that read as "watch" rather than "open". */
const WATCHABLE = new Set(["zoom_recording", "video"]);

/**
 * Mastermind library — recordings, cheat sheets, and templates for staff.
 *
 * Restricted to BL advisors and admins. The menu entry is hidden from other
 * roles, the route is guarded by RoleRoute, and the API enforces the same rule
 * on both the feed and the file stream (app/routers/resources.py), so a typed
 * URL or a direct API call gets a 403 rather than the content.
 *
 * Uploaded files are never exposed as blob URLs — openResourceFile() streams
 * them through an authenticated endpoint that re-checks the caller's role.
 */
export default function MastermindPage() {
  const [items, setItems] = useState<ResourceContent[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const toast = useToast();

  // Client-side, like the Glossary and FAQ: the staff library is small enough
  // to fetch whole. Matches the content-type label too, so "template" or
  // "zoom" finds every item of that kind.
  const visible = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      [
        item.title,
        item.body,
        item.category,
        item.content_type ? MASTERMIND_CONTENT_TYPE_LABELS[item.content_type] : null,
      ].some((field) => (field ?? "").toLowerCase().includes(q)),
    );
  }, [items, query]);

  useEffect(() => {
    fetchPublishedResources("mastermind")
      .then(setItems)
      .catch(() => setFailed(true));
  }, []);

  const handleFile = async (resource: ResourceContent, download: boolean) => {
    setBusyId(resource.id);
    try {
      await openResourceFile(resource, { download });
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't open that file. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="public-stage pb-8">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* Shared Dark · Gold interior hero — same header as AI Deal
            Matching and the rest of the interior pages. */}
        <div className="mb-8">
          <PageHero
            eyebrow="Staff Training"
            icon={GraduationCap}
            title="BL"
            titleAccent="Mastermind"
            description={"Recordings, cheat sheets, and templates from the BusinessLocating mastermind sessions."}
          />
        </div>

        {items && items.length > 0 && (
          <div className="mb-8 w-full sm:max-w-md">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search recordings, cheat sheets, templates..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        {failed ? (
          <EmptyPanel
            icon={TriangleAlert}
            title="Couldn't load Mastermind content"
            description="Something went wrong fetching this library. Please refresh the page to try again."
          />
        ) : items === null ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : visible.length === 0 ? (
          <EmptyPanel
            icon={GraduationCap}
            title={items.length === 0 ? "No Mastermind content yet" : "No matching content"}
            description={
              items.length === 0
                ? "Recordings and resources will appear here once they're published."
                : "Nothing here matches that search. Try a shorter phrase."
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((item) => {
              const typeLabel = item.content_type
                ? MASTERMIND_CONTENT_TYPE_LABELS[item.content_type] ?? item.content_type
                : null;
              const watchable = !!item.content_type && WATCHABLE.has(item.content_type);
              const busy = busyId === item.id;

              return (
                <article
                  key={item.id}
                  className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card/60 transition hover:border-primary/40 hover:shadow-sm"
                >
                  {/* Derived from the content type — every Mastermind card has
                      one, with the "other" image as the fallback. */}
                  <img
                    src={resourceThumbnail(item) ?? MASTERMIND_FALLBACK_THUMBNAIL}
                    alt=""
                    loading="lazy"
                    className="h-40 w-full object-cover"
                    onError={(e) => { e.currentTarget.src = MASTERMIND_FALLBACK_THUMBNAIL; }}
                  />
                  <div className="flex flex-1 flex-col p-5">
                    {item.category && (
                      <span className="inline-block text-[0.65rem] font-bold uppercase tracking-wide text-primary">
                        {item.category}
                      </span>
                    )}
                    <h3 className="mt-1 text-base font-bold text-foreground">{item.title}</h3>
                    {item.body && (
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
                        {item.body}
                      </p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[0.7rem] text-muted-foreground">
                      {typeLabel && (
                        <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-2.5 py-0.5 font-medium text-foreground">
                          {typeLabel}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 font-medium">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {formatPublishedDate(item.published_at)}
                      </span>
                      {item.has_file && item.file_size_bytes && (
                        <span>{formatFileSize(item.file_size_bytes)}</span>
                      )}
                    </div>

                    {/* Spacer pins the actions to the card bottom so all cards align */}
                    <div className="flex-1" />
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/50 pt-3.5">
                      {item.external_url ? (
                        <a
                          href={item.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
                        >
                          {watchable ? <PlayCircle className="h-3.5 w-3.5" /> : <ExternalLink className="h-3.5 w-3.5" />}
                          {watchable ? "Watch Recording" : "Open Link"}
                        </a>
                      ) : item.has_file ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleFile(item, false)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20 disabled:opacity-50"
                          >
                            {watchable ? <PlayCircle className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            {busy ? "Opening…" : watchable ? "Watch Recording" : "View"}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleFile(item, true)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Download
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
